import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './useAuth.jsx';
import {
    Menu, X, Play, Pause, Sparkles, MapPin, Mic, Eye, EyeOff,
    Search, Zap, Brain, User, UserPlus, Check, Lock, Mail, Globe
} from 'lucide-react';
import { DESTINATIONS } from './destinations.js';
import LearnMorePanel from './LearnMorePanel.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import './AuthCard.css';

export function AuthCard(props = {}) {
    const auth = useAuth() || {};
    const signInEmail = props.signInEmail || auth.signInEmail;
    const signUpEmail = props.signUpEmail || auth.signUpEmail;
    const signInGoogle = props.signInGoogle || auth.signInGoogle;
    const resetPassword = props.resetPassword || auth.resetPassword;
    const error = props.error !== undefined ? props.error : auth.error;
    const loading = props.loading !== undefined ? props.loading : auth.loading;
    const [mode, setMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isGooglePending, setIsGooglePending] = useState(false);
    const isGoogleAuthPendingRef = useRef(false);
    const [isResetPending, setIsResetPending] = useState(false);
    const isResetPendingRef = useRef(false);
    const [resetSuccessMessage, setResetSuccessMessage] = useState('');
    const [validationError, setValidationError] = useState('');
    const [isHudVisible, setIsHudVisible] = useState(window.innerWidth > 900);

    useEffect(() => {
        setValidationError('');
        setResetSuccessMessage('');
    }, [mode]);

    const getFriendlyError = (err) => {
        if (!err) return '';
        if (typeof err !== 'string') return 'An unexpected error occurred.';
        if (err.includes('auth/popup-closed-by-user') || err.includes('auth/cancelled-popup-request')) {
            return '';
        }
        if (err.includes('auth/popup-blocked')) {
            return 'Your browser blocked the Google sign-in window. Please allow popups for Wingman and try again.';
        }
        if (err.includes('auth/invalid-credential') || err.includes('auth/user-not-found') || err.includes('auth/wrong-password')) {
            return 'Invalid email or password.';
        }
        if (err.includes('auth/email-already-in-use')) {
            return 'Email is already registered. Please log in.';
        }
        if (err.includes('auth/weak-password')) {
            return 'Password must be at least 6 characters.';
        }
        if (err.includes('auth/invalid-email')) {
            return 'Please enter a valid email address.';
        }
        if (err.includes('auth/too-many-requests')) {
            return 'Too many failed attempts. Please try again later.';
        }
        return err.replace('Firebase: ', '').replace(/\(auth\/.*\)\.?/, '').trim() || 'Authentication failed.';
    };

    // Auto-rotate and Memory states
    const [bgIndex, setBgIndex] = useState(() => {
        const saved = localStorage.getItem('wingman_last_destination');
        return saved !== null && parseInt(saved) < DESTINATIONS.length ? parseInt(saved) : 0;
    });
    const [isAutoRotate, setIsAutoRotate] = useState(() => {
        const saved = localStorage.getItem('wingman_autorotate');
        return saved !== null ? JSON.parse(saved) : true;
    });

    const [manualOverride, setManualOverride] = useState(!isAutoRotate);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [isLearnMoreOpen, setIsLearnMoreOpen] = useState(false);
    const [promptIndex, setPromptIndex] = useState(0);

    const intervalRef = useRef(null);
    const idleTimeoutRef = useRef(null);
    const promptIntervalRef = useRef(null);

    // Persist preferences & last location
    useEffect(() => {
        localStorage.setItem('wingman_autorotate', JSON.stringify(!manualOverride));
        localStorage.setItem('wingman_last_destination', bgIndex.toString());
    }, [manualOverride, bgIndex]);

    // Handle Rotation and Idle Resume
    useEffect(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);

        if (isAutoRotate) {
            intervalRef.current = setInterval(() => {
                setBgIndex(prev => (prev + 1) % DESTINATIONS.length);
            }, 12000);
        } else if (!manualOverride) {
            idleTimeoutRef.current = setTimeout(() => {
                setIsAutoRotate(true);
            }, 30000);
        }

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
            if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        };
    }, [isAutoRotate, manualOverride, bgIndex]);

    useEffect(() => {
        promptIntervalRef.current = setInterval(() => {
            setPromptIndex(prev => (prev + 1) % 5);
        }, 4000);
        return () => clearInterval(promptIntervalRef.current);
    }, []);

    const handleSelectDestination = (index) => {
        setBgIndex(index);
        setIsAutoRotate(false);
        setIsDrawerOpen(false);
    };

    const toggleAutoRotate = () => {
        const nextState = !isAutoRotate;
        setIsAutoRotate(nextState);
        setManualOverride(!nextState);
    };

    const validateCredentials = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setValidationError('Please enter a valid email address.');
            return false;
        }
        if (password.length < 6) {
            setValidationError('Password must be at least 6 characters.');
            return false;
        }
        return true;
    };

    const handleSignIn = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setValidationError('');
        if (!validateCredentials()) return;

        setIsSubmitting(true);
        try {
            await signInEmail(email, password);
        } catch (err) {
            console.error("Auth Error:", err);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCreateAccount = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setValidationError('');

        // If credentials are typed, attempt signup directly
        if (email && password) {
            if (!validateCredentials()) return;
            setIsSubmitting(true);
            try {
                await signUpEmail(email, password);
            } catch (err) {
                console.error("Auth Error:", err);
            } finally {
                setIsSubmitting(false);
            }
        } else {
            // Otherwise, focus the email input so the user enters credentials
            setMode('signup');
            const emailInput = document.getElementById('email');
            if (emailInput) {
                emailInput.focus();
            }
        }
    };

    const handleResetPassword = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Immediate synchronous single-flight guard against concurrent clicks
        if (isResetPendingRef.current) {
            return;
        }

        setValidationError('');
        setResetSuccessMessage('');

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!email || !emailRegex.test(email)) {
            setValidationError('Please enter a valid email address.');
            return;
        }

        isResetPendingRef.current = true;
        setIsResetPending(true);

        try {
            if (typeof resetPassword === 'function') {
                await resetPassword(email);
            }
            setResetSuccessMessage('If an account exists for this email, password-reset instructions have been sent.');
        } catch (err) {
            const errStr = typeof err === 'string' ? err : (err?.message || err?.code || '');
            // Account enumeration protection: return neutral confirmation for user-not-found
            if (errStr.includes('auth/user-not-found')) {
                setResetSuccessMessage('If an account exists for this email, password-reset instructions have been sent.');
            } else if (errStr.includes('auth/invalid-email')) {
                setValidationError('Please enter a valid email address.');
            } else if (errStr.includes('auth/too-many-requests')) {
                setValidationError('Too many reset requests. Please wait a moment and try again.');
            } else {
                setResetSuccessMessage('If an account exists for this email, password-reset instructions have been sent.');
            }
        } finally {
            isResetPendingRef.current = false;
            setIsResetPending(false);
        }
    };

    const handleGoogleSignIn = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Immediate synchronous single-flight guard against concurrent clicks
        if (isGoogleAuthPendingRef.current) {
            return;
        }

        isGoogleAuthPendingRef.current = true;
        setIsGooglePending(true);
        setValidationError('');

        try {
            await signInGoogle();
        } catch (err) {
            const errStr = typeof err === 'string' ? err : (err?.message || err?.code || '');
            if (errStr.includes('auth/popup-closed-by-user') || errStr.includes('auth/cancelled-popup-request')) {
                console.info('[Auth] Google sign-in dismissed or superseded.');
            } else if (errStr.includes('auth/popup-blocked')) {
                console.warn('[Auth] Google sign-in popup blocked by browser.');
                setValidationError('Your browser blocked the Google sign-in window. Please allow popups for Wingman and try again.');
            } else {
                console.error('[Auth] Google Sign-In Error:', err);
                setValidationError(getFriendlyError(errStr) || 'Google sign-in failed. Please try again.');
            }
        } finally {
            isGoogleAuthPendingRef.current = false;
            setIsGooglePending(false);
        }
    };

    if (loading) return (
        <div className="auth-screen">
            <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="auth-loading-text"
            >
                INITIALIZING INTELLIGENCE LAYER...
            </motion.div>
        </div>
    );

    const activeDestination = DESTINATIONS[bgIndex];
    const nextDestination = DESTINATIONS[(bgIndex + 1) % DESTINATIONS.length];

    const displayError = validationError || getFriendlyError(error);

    return (
        <div className="auth-screen">
            {/* Hidden Preloader for next image */}
            <div style={{ display: 'none' }}>
                <img src={nextDestination.heroUrl} alt="preload" />
            </div>

            {/* Cinematic Background Carousel */}
            <div className="auth-background-carousel">
                <AnimatePresence>
                    {DESTINATIONS.map((dest, i) => i === bgIndex && (
                        <motion.div 
                            key={dest.id} 
                            initial={{ opacity: 0, scale: 1.05 }}
                            animate={{ opacity: 0.6, scale: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ opacity: { duration: 2 }, scale: { duration: 20, ease: 'linear' } }}
                            className="auth-bg-layer active"
                            style={{ backgroundImage: `url(${dest.heroUrl})` }}
                        />
                    ))}
                </AnimatePresence>
            </div>

            {/* Mobile Header (Hamburger) */}
            <motion.div 
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="auth-mobile-header"
            >
                <button className="auth-menu-btn" onClick={() => setIsDrawerOpen(true)} aria-label="Open destinations">
                    <Menu size={24} color="white" />
                </button>
                <div className="auth-mobile-title">
                    <span className="auth-mobile-location">{activeDestination.title}</span>
                    <span className="auth-mobile-country">{activeDestination.country}</span>
                </div>
            </motion.div>

            {/* Sidebar / Drawer */}
            <motion.div 
                className={`auth-sidebar ${isDrawerOpen ? 'drawer-open' : ''}`}
                initial={{ x: -50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
            >
                <div className="auth-sidebar-header">
                    <div className="auth-sidebar-title-group">
                        <h2>Destinations</h2>
                        <motion.button 
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="auth-rotate-toggle" 
                            onClick={toggleAutoRotate} 
                            title={isAutoRotate ? "Pause Auto-Rotate" : "Resume Auto-Rotate"}
                        >
                            {isAutoRotate ? <Pause size={14} /> : <Play size={14} />}
                            <span>{isAutoRotate ? "ON" : "OFF"}</span>
                        </motion.button>
                    </div>
                    <button className="auth-close-btn" onClick={() => setIsDrawerOpen(false)} aria-label="Close destinations">
                        <X size={24} color="white" />
                    </button>
                </div>
                
                <div className="auth-destination-list">
                    {DESTINATIONS.map((dest, i) => (
                        <motion.div 
                            key={dest.id}
                            whileHover={{ scale: 1.02, backgroundColor: 'rgba(255, 255, 255, 0.08)' }}
                            whileTap={{ scale: 0.98 }}
                            className={`auth-dest-card ${i === bgIndex ? 'active' : ''}`}
                            onClick={() => handleSelectDestination(i)}
                        >
                            <img src={dest.thumbUrl} alt={dest.title} loading="lazy" />
                            <div className="auth-dest-info">
                                <h3>{dest.title}</h3>
                                <span>{dest.country}</span>
                                <div className="auth-dest-meta">
                                    <span>{dest.time}</span> • <span>{dest.weather}</span>
                                    {dest.aiReady && <span className="auth-dest-ai">AI READY</span>}
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            {/* Overlay for mobile drawer */}
            <AnimatePresence>
                {isDrawerOpen && (
                    <motion.div 
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="auth-drawer-overlay" 
                        onClick={() => setIsDrawerOpen(false)} 
                    />
                )}
            </AnimatePresence>

            {/* Floating button to re-open HUD on mobile */}
            <AnimatePresence>
                {!isHudVisible && window.innerWidth <= 900 && (
                    <motion.button 
                        className="hud-fab"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 50 }}
                        onClick={() => setIsHudVisible(true)}
                    >
                        <Sparkles size={16} />
                        <span>SPATIAL INTEL</span>
                    </motion.button>
                )}
            </AnimatePresence>

            {/* Intelligence Briefing HUD */}
            <AnimatePresence>
                {isHudVisible && (
                    <motion.div 
                        className="intelligence-hud-wrapper"
                        initial={{ opacity: 0, y: 100 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 200 }}
                        transition={{ type: "spring", stiffness: 200, damping: 25 }}
                    >
                        <div className="intelligence-hud glassmorphic-panel">
                            <div className="hud-header">
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span className="hud-label">SPATIAL INTELLIGENCE</span>
                                    <motion.span 
                                        className="hud-pulse"
                                        animate={{ opacity: [0.4, 1, 0.4] }}
                                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                                    />
                                </div>
                                <button 
                                    className="hud-minimize-btn" 
                                    onClick={() => setIsHudVisible(false)}
                                    aria-label="Close Intelligence"
                                >
                                   <X size={16} />
                                </button>
                            </div>
                            
                            <div className="hud-body">
                                <div className="hud-title-group">
                                    <h2 className="hud-title">{activeDestination.title}</h2>
                                    <div className="hud-country">{activeDestination.country}</div>
                                </div>

                                <div className="hud-expandable default-visible">
                                    <div className="hud-stats-grid">
                                        <div className="hud-stat">
                                            <span className="hud-stat-label">POPULATION</span>
                                            <span className="hud-stat-value">{activeDestination.population}</span>
                                        </div>
                                        <div className="hud-stat">
                                            <span className="hud-stat-label">BEST SEASON</span>
                                            <span className="hud-stat-value">{activeDestination.bestSeason}</span>
                                        </div>
                                    </div>

                                    <div className="hud-insight">
                                        <span className="hud-insight-badge">WINGMAN INSIGHT</span>
                                        <p>{activeDestination.wingmanInsight}</p>
                                    </div>
                                    
                                    <button className="hud-learn-more" onClick={() => setIsLearnMoreOpen(true)}>
                                        LEARN MORE <span>→</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <LearnMorePanel 
                isOpen={isLearnMoreOpen} 
                onClose={() => setIsLearnMoreOpen(false)} 
                destination={activeDestination} 
            />

            <div className="auth-content-area">
                {/* Ambient background glows */}
                <div className="auth-glow-cyan" />
                <div className="auth-glow-magenta" />

                <motion.div 
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                    className="auth-card glassmorphic-panel wingman-2-card"
                >
                    {/* TOP BADGE: YOUR GATEWAY TO INTELLIGENT DISCOVERY */}
                    <div className="auth-top-badge-wrapper">
                        <span className="auth-top-pill">
                            YOUR GATEWAY TO INTELLIGENT DISCOVERY
                        </span>
                    </div>

                    {/* BRANDING: WINGMAN AI [2.0] */}
                    <div className="auth-brand-header">
                        <div className="auth-title-row">
                            <h1 className="auth-brand-title">WINGMAN AI</h1>
                            <span className="auth-version-pill">2.0</span>
                        </div>
                        <p className="auth-brand-tagline">
                            The Intelligence Layer for Your World
                        </p>
                    </div>

                    {/* CAPABILITY STRIP: SPATIAL • VOICE • VISION • RESEARCH • REAL-TIME • CONTEXT */}
                    <div className="auth-capability-strip">
                        <div className="cap-item">
                            <MapPin size={13} className="cap-icon cap-spatial" />
                            <span>SPATIAL</span>
                        </div>
                        <div className="cap-item">
                            <Mic size={13} className="cap-icon cap-voice" />
                            <span>VOICE</span>
                        </div>
                        <div className="cap-item">
                            <Eye size={13} className="cap-icon cap-vision" />
                            <span>VISION</span>
                        </div>
                        <div className="cap-item">
                            <Search size={13} className="cap-icon cap-research" />
                            <span>RESEARCH</span>
                        </div>
                        <div className="cap-item">
                            <Zap size={13} className="cap-icon cap-realtime" />
                            <span>REAL-TIME</span>
                        </div>
                        <div className="cap-item">
                            <Brain size={13} className="cap-icon cap-context" />
                            <span>CONTEXT</span>
                        </div>
                    </div>

                    {/* ══════════════════════════════════════════════════════════
                        NEW-USER CARD
                       ══════════════════════════════════════════════════════════ */}
                    <div className="new-user-box">
                        <div className="new-pill-badge">NEW</div>

                        <div className="new-user-graphic" aria-hidden="true">
                            <img 
                                src="/wingman-globe.png" 
                                alt="" 
                                className="new-user-globe-asset" 
                                loading="lazy"
                            />
                        </div>

                        <div className="new-user-header">
                            <div className="new-user-avatar">
                                <UserPlus size={20} />
                            </div>
                            <div className="new-user-header-text">
                                <h2 className="new-user-title">
                                    New to Wingman <span className="highlight-cyan">2.0?</span>
                                </h2>
                                <p className="new-user-subtitle">
                                    Create your <strong className="highlight-bold">FREE</strong> Wingman account and start exploring a more intelligent world.
                                </p>
                            </div>
                        </div>

                        <div className="new-user-body">
                            <ul className="new-user-checklist">
                                <li>
                                    <Check size={14} className="check-icon" />
                                    <span>Free plan with 25 AI credits</span>
                                </li>
                                <li>
                                    <Check size={14} className="check-icon" />
                                    <span>Spatial & destination discovery</span>
                                </li>
                                <li>
                                    <Check size={14} className="check-icon" />
                                    <span>Voice + multimodal experiences</span>
                                </li>
                                <li>
                                    <Check size={14} className="check-icon" />
                                    <span>No credit card required</span>
                                </li>
                            </ul>
                        </div>

                        <button 
                            type="button" 
                            onClick={handleCreateAccount}
                            className="auth-cta create-account-cta" 
                            disabled={isSubmitting}
                            aria-busy={isSubmitting}
                        >
                            {isSubmitting && mode === 'signup' ? 'CREATING ACCOUNT...' : 'CREATE FREE ACCOUNT →'}
                        </button>
                    </div>

                    {/* DIVIDER: OR */}
                    <div className="auth-section-divider">
                        <span className="auth-divider-line"></span>
                        <span className="auth-divider-text">OR</span>
                        <span className="auth-divider-line"></span>
                    </div>

                    {/* ══════════════════════════════════════════════════════════
                        RETURNING-USER / RECOVERY SECTION
                       ══════════════════════════════════════════════════════════ */}
                    <div className="returning-user-box">
                        <div className="returning-user-header">
                            <div className="returning-user-avatar">
                                {mode === 'recovery' ? <Lock size={20} /> : <User size={20} />}
                            </div>
                            <div className="returning-user-header-text">
                                <h3 className="returning-user-title">
                                    {mode === 'recovery' ? 'Reset Password' : 'Already have a Wingman account?'}
                                </h3>
                                <p className="returning-user-subtitle">
                                    {mode === 'recovery'
                                        ? "Enter your email address and we'll send you instructions to reset your password."
                                        : 'Sign in to access your destinations, saved places, and settings.'}
                                </p>
                            </div>
                        </div>

                        {mode === 'recovery' ? (
                            <form onSubmit={handleResetPassword} className="auth-form" noValidate>
                                <div className="input-field-group">
                                    <div className="input-wrapper">
                                        <Mail size={16} className="input-icon" aria-hidden="true" />
                                        <input 
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="Email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            aria-label="Email Address"
                                            aria-invalid={!!displayError}
                                            className="auth-text-input"
                                        />
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {displayError && (
                                        <motion.div 
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="auth-error-banner"
                                            role="alert"
                                            aria-live="assertive"
                                        >
                                            {displayError}
                                        </motion.div>
                                    )}
                                    {resetSuccessMessage && (
                                        <motion.div 
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="auth-success-banner"
                                            role="status"
                                            aria-live="polite"
                                        >
                                            {resetSuccessMessage}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <button 
                                    type="submit" 
                                    onClick={handleResetPassword}
                                    className="auth-cta sign-in-cta" 
                                    disabled={isResetPending}
                                    aria-busy={isResetPending}
                                >
                                    {isResetPending ? 'SENDING RESET LINK...' : 'SEND RESET LINK →'}
                                </button>

                                <button 
                                    type="button" 
                                    onClick={() => { setMode('login'); setValidationError(''); setResetSuccessMessage(''); }}
                                    className="auth-back-to-login-btn"
                                >
                                    ← Back to Sign In
                                </button>
                            </form>
                        ) : (
                            <form onSubmit={handleSignIn} className="auth-form" noValidate>
                                <div className="input-field-group">
                                    <div className="input-wrapper">
                                        <Mail size={16} className="input-icon" aria-hidden="true" />
                                        <input 
                                            id="email"
                                            name="email"
                                            type="email"
                                            placeholder="Email address"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            required
                                            aria-label="Email Address"
                                            aria-invalid={!!displayError}
                                            className="auth-text-input"
                                        />
                                    </div>

                                    <div className="input-wrapper">
                                        <Lock size={16} className="input-icon" aria-hidden="true" />
                                        <input 
                                            id="password"
                                            name="password"
                                            type={showPassword ? "text" : "password"}
                                            placeholder="Password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            aria-label="Password"
                                            aria-invalid={!!displayError}
                                            className="auth-text-input"
                                        />
                                        <button 
                                            type="button" 
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="password-toggle-btn"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                        >
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>

                                <div className="forgot-password-row">
                                    <button 
                                        type="button" 
                                        onClick={() => { setMode('recovery'); setValidationError(''); setResetSuccessMessage(''); }}
                                        className="forgot-password-link"
                                    >
                                        Forgot Password?
                                    </button>
                                </div>

                                <AnimatePresence>
                                    {displayError && (
                                        <motion.div 
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="auth-error-banner"
                                            role="alert"
                                            aria-live="assertive"
                                        >
                                            {displayError}
                                        </motion.div>
                                    )}
                                </AnimatePresence>

                                <button 
                                    type="submit" 
                                    onClick={handleSignIn}
                                    className="auth-cta sign-in-cta" 
                                    disabled={isSubmitting}
                                    aria-busy={isSubmitting}
                                >
                                    {isSubmitting && mode === 'login' ? 'SIGNING IN...' : 'SIGN IN TO WINGMAN →'}
                                </button>
                            </form>
                        )}

                        {/* SOCIAL SSO: OR CONTINUE WITH */}
                        <div className="auth-social-divider">
                            <span className="auth-divider-line"></span>
                            <span className="auth-divider-text">OR CONTINUE WITH</span>
                            <span className="auth-divider-line"></span>
                        </div>

                        <div className="auth-social-row">
                            <motion.button 
                                whileHover={!isGooglePending ? { scale: 1.02, backgroundColor: 'rgba(255,255,255,0.1)' } : {}}
                                whileTap={!isGooglePending ? { scale: 0.98 } : {}}
                                type="button"
                                onClick={handleGoogleSignIn}
                                disabled={isGooglePending || isSubmitting}
                                aria-busy={isGooglePending}
                                className={`auth-social-btn ${isGooglePending ? 'pending' : ''}`}
                            >
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                                    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.616z" fill="#4285F4"/>
                                    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                                    <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                                    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
                                </svg>
                                <span>{isGooglePending ? 'Connecting to Google...' : 'Continue with Google'}</span>
                            </motion.button>

                            <button 
                                type="button"
                                className="auth-social-btn disabled"
                                disabled 
                                title="Coming Soon"
                            >
                                <svg width="18" height="18" viewBox="0 0 170 170" fill="currentColor" aria-hidden="true">
                                    <path d="M150.37 130.25c-2.45 5.66-5.35 10.87-8.71 15.66-4.58 6.53-8.33 11.05-11.22 13.56-4.48 4.12-9.28 6.23-14.42 6.35-3.69 0-8.14-1.05-13.32-3.18-5.19-2.12-9.97-3.17-14.34-3.17-4.58 0-9.49 1.05-14.75 3.17-5.26 2.13-9.5 3.24-12.74 3.35-4.35.13-9.16-1.9-14.42-6.08-3.69-3.04-7.67-7.81-11.96-14.34-6.86-10.45-12.28-21.78-16.27-34-4-12.22-6-23.75-6-34.61 0-14.36 3.5-26.31 10.5-35.85 7-9.55 15.86-14.42 26.58-14.63 4.13 0 9.07 1.15 14.82 3.44 5.75 2.29 9.38 3.5 10.89 3.65 2.12-.22 5.86-1.47 11.22-3.75 5.36-2.28 9.94-3.32 13.75-3.12 11.08.76 20.37 5.11 27.87 13.06-9.78 5.87-14.56 14.13-14.34 24.78.22 8.26 3.42 15.22 9.62 20.87 6.2 5.65 13.69 9.02 22.48 10.11-2.07 6.09-4.78 12.39-8.15 18.91zM119.22 33.15c0-6.85 2.45-13.37 7.35-19.57 4.9-6.2 10.97-10.5 18.22-12.91.43 1.09.65 2.28.65 3.59 0 6.85-2.5 13.43-7.5 19.73-5 6.3-11.25 10.65-18.72 13.05v-3.89z"/>
                                </svg>
                                <span>Continue with Apple</span>
                            </button>
                        </div>

                        {/* INTERNAL S&G LAB DEVELOPER LOGIN */}
                        {(import.meta.env.DEV || (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'))) && (
                            <div className="auth-dev-access-wrapper">
                                <button
                                    type="button"
                                    onClick={() => {
                                        sessionStorage.setItem('wingman_cert_auth_active', 'true');
                                        window.location.href = '/';
                                    }}
                                    className="auth-dev-access-btn"
                                >
                                    <span>⚡</span> S&G AI LAB DEVELOPER ACCESS (TEST MODE)
                                </button>
                            </div>
                        )}

                        {/* TRUST BADGES STRIP */}
                        <div className="auth-trust-strip">
                            <span className="trust-badge-item">
                                <span className="trust-icon">🔒</span> SECURE
                            </span>
                            <span className="trust-badge-item">
                                <span className="trust-icon">🌐</span> GLOBAL
                            </span>
                            <span className="trust-badge-item">
                                <span className="trust-icon">⚡</span> REAL-TIME
                            </span>
                            <span className="trust-badge-item">
                                <span className="trust-icon">🧠</span> AI POWERED
                            </span>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
}
